import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "./AuthShell";
import { api, tokenStore } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, otp });
      tokenStore.set(data.access_token, data.refresh_token);
      await refresh();
      toast.success("Email verified — welcome!");
      nav("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new OTP has been sent to your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend OTP");
    } finally { setResending(false); }
  };

  return (
    <AuthShell title="Verify your email" subtitle={`Enter the 6-digit code sent to ${email}`}>
      <form onSubmit={submit} className="space-y-5">
        <input
          data-testid={AUTH.otpInput}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
          maxLength={6}
          placeholder="000000"
          autoFocus
          className="w-full text-center tracking-[0.6em] text-2xl font-display border border-gray-300 rounded-md px-3 py-4 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
        />
        <button
          data-testid={AUTH.otpSubmit}
          disabled={submitting || otp.length < 6}
          className="w-full bg-[#538135] text-white hover:bg-[#09582D] transition-colors rounded-md px-4 py-3 text-sm font-semibold disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify email"}
        </button>
        <button
          type="button"
          onClick={resend}
          data-testid={AUTH.otpResend}
          disabled={resending}
          className="w-full text-sm text-[#538135] hover:text-[#09582D] font-medium"
        >
          {resending ? "Resending…" : "Resend code"}
        </button>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            <span className="font-semibold">Important:</span> After creating your account,
            we'll send a verification email with a one-time password (OTP). If you don't
            receive it within a few minutes, please check your <span className="font-semibold">Spam/Junk</span>
            folder and mark the email as <span className="font-semibold">"Not Spam"</span> to ensure
            future SQIT.ai™ emails are delivered to your inbox.
          </p>
        </div>
      </form>
    </AuthShell>
  );
}
